import React, { useState } from 'react'
import { createStyles, makeStyles } from '@mui/styles'
import { Box, Button, Paper, Theme, Typography, TextField } from '@mui/material'
import Env from '../../shared/config/envConfig'
import { useSelector } from 'react-redux'
import { getEventSettings } from '../../store/settings/settings.selectors'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 'calc(100% - 32px)',
      padding: theme.spacing(2),
      margin: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        margin: 0,
        padding: theme.spacing(2),
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
    formContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      maxWidth: '400px',
      margin: '20px 0',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    responseContainer: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[100],
      borderRadius: '4px',
    },
  }),
)

interface CheckinResponse {
  helperName: string
  employmentName: string
  time: string
  type: 'CHECKIN' | 'CHECKOUT'
}

const Checkin: React.FC = () => {
  const classes = useStyles()

  const { eventTitle } = useSelector(getEventSettings)

  const [kennung, setKennung] = useState('')
  const [response, setResponse] = useState<CheckinResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleCheckin = () => {
    if (kennung.length !== 6) {
      setError('Bitte gib deine 6-stellige Kennung ein.')
      return
    }

    setLoading(true)
    setError(null)
    setResponse(null)

    const restUrl = `${Env.basePath}/api/checkin`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    fetch(restUrl, {
      method: 'POST',
      credentials: 'include',
      headers: requestHeaders,
      body: JSON.stringify({
        kennung,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}))
          throw new Error(errData.message || 'Check-in/out fehlgeschlagen.')
        }
        return res.json()
      })
      .then((data: CheckinResponse) => {
        setResponse(data)
        setKennung('')
      })
      .catch((err) => {
        setError(err.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Paper className={classes.root}>
      <Typography variant='h5' gutterBottom>
        Check-in / Check-out
      </Typography>
      <Typography variant='body1'>
        Willkommen beim {eventTitle}! Bitte gib deine 6-stellige Nutzerkennung ein, um dich ein- oder auszuchecken.
      </Typography>

      <Box className={classes.formContainer}>
        <TextField
          label='Nutzerkennung'
          variant='outlined'
          value={kennung}
          onChange={(e) => setKennung(e.target.value)}
          inputProps={{ maxLength: 6 }}
          error={!!error}
          helperText={error}
          fullWidth
        />
        <Button
          variant='contained'
          color='primary'
          onClick={handleCheckin}
          disabled={loading || !kennung || kennung.length !== 6}
          fullWidth
        >
          {loading ? 'Verarbeite...' : 'Einchecken / Auschecken'}
        </Button>
      </Box>

      {response && (
        <Box className={classes.responseContainer}>
          <Typography variant='h6' color='primary' gutterBottom>
            Erfolgreich {response.type === 'CHECKIN' ? 'eingecheckt' : 'ausgecheckt'}!
          </Typography>
          <Typography variant='body1'>
            <b>Name:</b> {response.helperName}
          </Typography>
          <Typography variant='body1'>
            <b>Einsatz:</b> {response.employmentName}
          </Typography>
          <Typography variant='body1'>
            <b>Zeitpunkt:</b> {new Date(response.time).toLocaleString('de-DE')}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default Checkin
